/**
 * Coluna CENTRAL do /chats: header com ações por estado (Assumir / Ações /
 * Adicionar ticket / avaliação), thread com polling incremental 4s e composer
 * com comentário interno (Enter envia, Shift+Enter quebra).
 */
import { Fragment, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRightLeft,
  Ban,
  Bot,
  Check,
  CheckCheck,
  CheckCircle2,
  ChevronDown,
  Download,
  FileText,
  Hand,
  Info,
  LogOut,
  Paperclip,
  Pencil,
  Plus,
  RotateCcw,
  SendHorizonal,
  X,
} from 'lucide-react';
import type { ChatDetail, ChatMessage, ChatTranscriptFormat, DeliveryStatus } from '@erp/shared';
import type { ApiError } from '@/lib/api-client';
import { deliveryView, MAX_UPLOAD_BYTES } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input, Label } from '@/components/ui/input';
import { Dialog, EmptyState, Spinner } from '@/components/ui/misc';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthUser } from '@/auth/use-auth';
import { usePermissions } from '@/auth/use-permissions';
import { cn, formatDateTime } from '@/lib/utils';
import { useRealtimeConnected } from '@/lib/realtime';
import { dayLabel, startsNewDay } from './chat-day';
import { formatFileSize } from '@/features/knowledge/knowledge-format';
import { chatDetailOptions, useChatMessages, whatsappWindowOptions } from './chats-queries';
import {
  downloadChatTranscript,
  useAssumeChat,
  useLeaveChat,
  useMarkChatRead,
  useResendChatMessage,
  useResumeChat,
  useSendChatMedia,
  useSendChatMessage,
  useSendChatTemplate,
  type ChatTemplateBody,
  type AssumeChatInput,
} from './chats-mutations';
import { LinkifiedText, MessageMedia, isMediaContent } from './chat-media';
import { BotMessageBody, WhatsappText } from './whatsapp-text';
import {
  hasSystemNotes,
  messageKind,
  noteSummary,
  rememberSystemDetails,
  rememberedSystemDetails,
} from './message-kind';
import { TemplatePicker } from './whatsapp-template-picker';
import { useQuickReplyComposer } from './quick-reply-picker';
import { useTransientToast } from '@/components/ui/transient-toast';
import {
  ChannelChip,
  ChatAvatar,
  ExternalBotBadge,
  MAX_UPLOAD_LABEL,
  OriginBadge,
  Stars,
  StatusBadge,
  WaitingAttendantBadge,
  chatDisplayName,
  deliveryErrorText,
  errorMessage,
  formatHourMinute,
  formatWaPhone,
} from './chats-format';
import { ArchiveChatDialog, LinkTicketDialog, TransferChatDialog } from './chat-dialogs';
import { ContactSheet } from './contact-sheet';

export function ChatWindow({
  chatId,
  infoOpen,
  onToggleInfo,
  onClearSelection,
  onSelectChat,
}: {
  chatId: number | null;
  infoOpen: boolean;
  onToggleInfo: () => void;
  /** limpa a seleção (volta ao estado vazio) — ex.: chat saiu da visibilidade */
  onClearSelection: () => void;
  /** abre OUTRA conversa — ex.: retomar achou uma aberta do mesmo contato (409) */
  onSelectChat: (chatId: number) => void;
}) {
  // Toast aqui (e não na conversa): sobrevive ao desmonte da conversa quando a
  // transferência tira o chat da visibilidade e a seleção é limpa.
  const { toast, showToast } = useTransientToast();
  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border bg-card shadow-xs">
      {chatId === null ? (
        <EmptyState message="Selecione uma conversa" className="h-full" />
      ) : (
        <ChatConversation
          key={chatId}
          chatId={chatId}
          infoOpen={infoOpen}
          onToggleInfo={onToggleInfo}
          onGone={onClearSelection}
          onSelectChat={onSelectChat}
          showToast={showToast}
        />
      )}
      {toast}
    </div>
  );
}

/**
 * 404 do detalhe: o chat deixou de existir PARA ESTE USUÁRIO — tipicamente
 * transferido para um departamento fora da visibilidade dele com a janela
 * aberta. Não é erro a exibir: a seleção é limpa e a UI volta ao estado vazio.
 */
export function isChatGone(e: unknown): boolean {
  return (
    e !== null && typeof e === 'object' && 'status' in e && (e as { status: unknown }).status === 404
  );
}

function ChatConversation({
  chatId,
  infoOpen,
  onToggleInfo,
  onGone,
  onSelectChat,
  showToast,
}: {
  chatId: number;
  infoOpen: boolean;
  onToggleInfo: () => void;
  /** o chat sumiu da visibilidade do usuário (404 no detalhe) — limpar seleção */
  onGone: () => void;
  onSelectChat: (chatId: number) => void;
  showToast: (message: string) => void;
}) {
  const me = useAuthUser();
  // SSE conectado ⇒ chat.updated invalida o detalhe; polling vira fallback.
  const realtimeUp = useRealtimeConnected();
  const detailQ = useQuery({
    ...chatDetailOptions(chatId),
    refetchInterval: realtimeUp ? 60_000 : 8_000,
    // 404 é terminal (chat fora da visibilidade) — repetir só atrasa o
    // degrade gracioso do efeito abaixo.
    retry: (failureCount, error) => !isChatGone(error) && failureCount < 3,
  });

  // Degrade gracioso: transferido para fora da visibilidade com a janela
  // aberta ⇒ o refetch do detalhe 404a. Limpa a seleção (estado vazio) em vez
  // de exibir um erro duro.
  const chatGone = detailQ.isError && isChatGone(detailQ.error);
  useEffect(() => {
    if (chatGone) onGone();
  }, [chatGone, onGone]);
  // origin habilita o resync de ticks do WhatsApp dentro do hook (v6). O fetch
  // fica AQUI (e não dentro do ChatThread) para sair em paralelo com o detalhe.
  const messagesQ = useChatMessages(chatId, detailQ.data?.origin);

  const chat = detailQ.data;
  const isMine = chat?.responsible != null && me != null && chat.responsible.id === me.id;

  if (detailQ.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="size-6" />
      </div>
    );
  }
  if (detailQ.isError || !chat) {
    // Chat fora da visibilidade: o efeito acima limpa a seleção — nada a
    // renderizar neste frame (sem flash de erro duro).
    if (chatGone) return null;
    return (
      <EmptyState
        message={`Erro ao carregar a conversa: ${errorMessage(detailQ.error)}`}
        className="h-full"
      />
    );
  }

  return (
    <>
      <ChatHeader
        chat={chat}
        isMine={isMine}
        infoOpen={infoOpen}
        onToggleInfo={onToggleInfo}
        onSelectChat={onSelectChat}
        showToast={showToast}
      />

      <ChatThread
        chatId={chatId}
        messages={messagesQ.data}
        isPending={messagesQ.isPending}
        // spec 028: "tentar de novo" só para quem pode responder agora
        canResend={chat.origin === 'whatsapp' && chat.status === 'in_attendance' && isMine}
        whatsapp={chat.origin === 'whatsapp'}
      />

      <ChatComposer chat={chat} isMine={isMine} />
    </>
  );
}

// ── Thread ──────────────────────────────────────────────────────────────────

/** Referência estável para o caso "ainda sem dados" (evita re-render por identidade). */
const NO_MESSAGES: ChatMessage[] = [];

/**
 * Corpo da conversa: bolhas + auto-scroll + marcação de leitura. Recebe o
 * resultado de `useChatMessages` em vez de buscá-lo — quem monta a janela
 * dispara o fetch junto com o do detalhe. Usado pela janela do /chats E pela
 * aba "Conversas" do ticket (v12), que assim compartilham a MESMA renderização
 * de mídia/legenda/ticks.
 */
export function ChatThread({
  chatId,
  messages,
  isPending,
  className,
  canResend = false,
  whatsapp = false,
}: {
  chatId: number;
  messages: ChatMessage[] | undefined;
  isPending: boolean;
  className?: string;
  /** spec 028: quem segura a conversa pode reenviar a resposta que falhou */
  canResend?: boolean;
  /** conversa do WhatsApp: o texto sai com a formatação de lá (`*negrito*`…) */
  whatsapp?: boolean;
}) {
  const list = messages ?? NO_MESSAGES;
  // spec 060: as notas do sistema começam recolhidas; o switch mostra todas, e a
  // escolha fica neste navegador
  const [systemDetails, setSystemDetails] = useState(() =>
    rememberedSystemDetails(() => window.localStorage)
  );
  const toggleSystemDetails = (on: boolean) => {
    rememberSystemDetails(() => window.localStorage, on);
    setSystemDetails(on);
  };
  const showSwitch = useMemo(() => hasSystemNotes(list), [list]);
  const resend = useResendChatMessage(chatId);
  const [resendError, setResendError] = useState<string | null>(null);
  const onResend = canResend
    ? (messageId: number) => {
        setResendError(null);
        resend.mutate(messageId, { onError: (e) => setResendError(errorMessage(e)) });
      }
    : null;

  // Marca como lida ao abrir e sempre que chega mensagem nova do cliente.
  const markRead = useMarkChatRead();
  const markReadRef = useRef(markRead.mutate);
  markReadRef.current = markRead.mutate;
  const lastInboundId = useMemo(
    () => list.filter((m) => m.direction === 'inbound').at(-1)?.id ?? null,
    [list]
  );
  useEffect(() => {
    markReadRef.current(chatId);
  }, [chatId, lastInboundId]);

  // Auto-scroll para o fim quando entram mensagens novas.
  const threadRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [list.length, chatId]);

  return (
    <div
      ref={threadRef}
      className={cn(
        'min-h-0 flex-1 space-y-2 overflow-y-auto bg-background px-4 py-3',
        className
      )}
    >
      {showSwitch && !isPending ? (
        <div
          data-system-details-switch
          className="sticky -top-3 z-10 -mx-4 -mt-3 flex justify-end bg-background/95 px-4 py-1.5 backdrop-blur"
        >
          <span className="flex items-center gap-1.5">
            <Switch
              id={`chat-system-details-${chatId}`}
              size="sm"
              checked={systemDetails}
              onCheckedChange={toggleSystemDetails}
            />
            <Label htmlFor={`chat-system-details-${chatId}`} className="text-xs text-muted-foreground">
              Detalhes do sistema
            </Label>
          </span>
        </div>
      ) : null}
      {isPending ? (
        <div className="flex h-full items-center justify-center">
          <Spinner className="size-5" />
        </div>
      ) : list.length === 0 ? (
        <EmptyState message="Nenhuma mensagem ainda" className="h-full" />
      ) : (
        list.map((m, i) => (
          <Fragment key={m.id}>
            {/* spec 068 (D1): o dia abre a conversa e cada troca de dia */}
            {startsNewDay(list[i - 1]?.createdAt, m.createdAt) ? <DaySeparator at={m.createdAt} /> : null}
            <MessageBubble
              message={m}
              whatsapp={whatsapp}
              systemDetails={systemDetails}
              onResend={onResend}
              resending={resend.isPending && resend.variables === m.id}
            />
          </Fragment>
        ))
      )}
      {resendError ? (
        <p className="text-center text-xs text-destructive" role="alert">
          {resendError}
        </p>
      ) : null}
    </div>
  );
}

/** spec 068 (D1): "Hoje", "Ontem" ou "seg., 27/10/2026", no fuso de quem olha */
function DaySeparator({ at }: { at: string | Date }) {
  return (
    <div className="flex justify-center py-1" data-chat-day>
      <span className="rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground">
        {dayLabel(new Date(at), new Date())}
      </span>
    </div>
  );
}

/** a hora da mensagem; passar o mouse mostra data e hora (spec 068, D1) */
function MessageTime({ at }: { at: string | Date }) {
  return (
    <time dateTime={new Date(at).toISOString()} title={formatDateTime(at)}>
      {formatHourMinute(at)}
    </time>
  );
}

// ── Header ──────────────────────────────────────────────────────────────────

/**
 * Ações por estado (spec 013 — ninguém fica preso):
 * - FILA (inclusive no meio do chatbot): Assumir, e Finalizar/Cancelar direto
 *   — encerrar da fila para o bot e tira a conversa de lá;
 * - MINHA em atendimento: Transferir, Sair, Finalizar/Cancelar;
 * - de OUTRO atendente: com "Gerenciar conversas de outros", Assumir desta
 *   pessoa e Finalizar/Cancelar; sem ela, só leitura;
 * - ENCERRADA: Retomar atendimento (409 ⇒ abre a conversa aberta do contato);
 * - sempre: Baixar conversa (o PDF) e, ao lado, o texto puro (.txt).
 * A API é quem barra de verdade; aqui só escondemos o que ela recusaria.
 */
function ChatHeader({
  chat,
  isMine,
  infoOpen,
  onToggleInfo,
  onSelectChat,
  showToast,
}: {
  chat: ChatDetail;
  isMine: boolean;
  infoOpen: boolean;
  onToggleInfo: () => void;
  onSelectChat: (chatId: number) => void;
  showToast: (message: string) => void;
}) {
  const assume = useAssumeChat();
  const leave = useLeaveChat();
  const resume = useResumeChat();
  // spec 056: o painel "Contato", aberto pelo nome
  const [contactOpen, setContactOpen] = useState(false);
  // `ready` evita o item aparecer (allow-all do /me pendente) e sumir em seguida.
  const { can, ready } = usePermissions();
  const [dialog, setDialog] = useState<'transfer' | 'finish' | 'cancel' | 'ticket' | null>(null);
  const [downloading, setDownloading] = useState(false);

  const archived = chat.status === 'archived';
  const inbox = chat.status === 'inbox';
  const mineAttending = chat.status === 'in_attendance' && isMine;
  const othersAttending = chat.status === 'in_attendance' && !isMine;
  const manageOthers = ready && can('chat.manage_others');
  const canClose = inbox || mineAttending || (othersAttending && manageOthers);
  const canCancel = canClose && ready && can('chat.cancel');
  const deskName = chat.desk?.name ?? null;

  const onActionError = (e: unknown) => showToast(errorMessage(e));

  // spec 015: em pausa, a API recusa o assumir com 409 attendant_paused e a
  // tela PERGUNTA — assumir sem decidir deixaria a pausa correndo com a
  // pessoa atendendo, e o relatório contaria as duas coisas ao mesmo tempo
  const [pausedAssume, setPausedAssume] = useState<Exclude<AssumeChatInput, number> | null>(null);
  const doAssume = (input: Exclude<AssumeChatInput, number>) =>
    assume.mutate(input, {
      onSuccess: () => setPausedAssume(null),
      onError: (e) => {
        const err = e as unknown as ApiError;
        if (err.status === 409 && err.error === 'attendant_paused' && !input.endPause) {
          setPausedAssume(input);
          return;
        }
        setPausedAssume(null);
        onActionError(e);
      },
    });

  const doResume = () =>
    resume.mutate(chat.id, {
      onError: (e) => {
        const err = e as unknown as ApiError;
        const openId = (err.details as { id?: number } | undefined)?.id;
        if (err.status === 409 && err.error === 'chat_already_open' && typeof openId === 'number') {
          showToast('Este contato já tem uma conversa aberta — abrindo ela.');
          onSelectChat(openId);
          return;
        }
        onActionError(e);
      },
    });

  const doDownload = (format: ChatTranscriptFormat) => {
    setDownloading(true);
    downloadChatTranscript(chat.id, format)
      .catch(() => showToast('Não foi possível baixar a conversa.'))
      .finally(() => setDownloading(false));
  };

  // subtítulo: cliente · telefone · departamento, + o estado do encerramento
  const displayName = chatDisplayName(chat);
  const contact = formatWaPhone(chat.waContact);
  const subtitle = [chat.client?.name, contact !== displayName ? contact : null, deskName]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="flex shrink-0 items-center gap-2.5 border-b px-4 py-2.5">
      <ChatAvatar name={displayName} />
      <div className="min-w-0 flex-1">
        {/* flex-wrap: com os selos (spec 013/021) a linha não cabe na coluna
            do meio — sem quebrar, o nome encolhia até sumir */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <button
            type="button"
            onClick={() => setContactOpen(true)}
            title="Editar contato"
            aria-haspopup="dialog"
            className="group inline-flex min-w-0 items-center gap-1 rounded-sm text-left text-sm font-semibold hover:text-primary focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
          >
            <span className="truncate">{displayName}</span>
            <Pencil
              aria-hidden
              className="size-3 shrink-0 text-muted-foreground opacity-60 group-hover:opacity-100"
            />
          </button>
          <OriginBadge origin={chat.origin} className="px-1.5 py-0 text-[10px]" />
          <StatusBadge
            status={chat.status}
            canceled={chat.canceled}
            className="px-1.5 py-0 text-[10px]"
          />
          {chat.externalBotActive ? <ExternalBotBadge /> : null}
          {inbox && chat.waitingAttendantSince ? <WaitingAttendantBadge /> : null}
          {chat.chatbotActive ? (
            <span className="text-[10px] font-medium text-muted-foreground">chatbot em andamento</span>
          ) : null}
        </div>
        {/* spec 028 (FR-003): por qual número da empresa a pessoa fala */}
        {chat.channel ? <ChannelChip channel={chat.channel} className="max-w-full text-xs" /> : null}
        {subtitle || (archived && chat.closeReason) ? (
          <div className="truncate text-xs text-muted-foreground">
            {/* cliente · telefone WhatsApp (E.164, v6) · departamento (v8) */}
            {subtitle}
            {archived && chat.closeReason ? (
              <>
                {subtitle ? ' · ' : ''}
                Motivo: <span className="text-foreground">{chat.closeReason.name}</span>
                {chat.closedBy ? ` (por ${chat.closedBy.name})` : ''}
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {inbox ? (
          <Button
            size="sm"
            onClick={() => doAssume({ chatId: chat.id })}
            disabled={assume.isPending}
          >
            {assume.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
            Assumir conversa
          </Button>
        ) : null}

        {archived ? (
          <Button size="sm" onClick={doResume} disabled={resume.isPending}>
            {resume.isPending ? (
              <Spinner className="size-4 border-primary-foreground" />
            ) : (
              <RotateCcw />
            )}
            Retomar atendimento
          </Button>
        ) : null}

        {mineAttending && chat.ticketNumber === null ? (
          <Button variant="outline" size="sm" onClick={() => setDialog('ticket')}>
            <Plus />
            Adicionar ticket
          </Button>
        ) : null}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              Ações
              <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {mineAttending ? (
              <>
                <DropdownMenuItem onSelect={() => setDialog('transfer')}>
                  <ArrowRightLeft />
                  Transferir
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={leave.isPending}
                  onSelect={() => leave.mutate(chat.id, { onError: onActionError })}
                >
                  <LogOut />
                  Sair da conversa
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            ) : null}
            {othersAttending && manageOthers ? (
              <>
                <DropdownMenuItem
                  disabled={assume.isPending}
                  onSelect={() => doAssume({ chatId: chat.id, takeover: true })}
                >
                  <Hand />
                  Assumir de {chat.responsible?.name ?? 'outro atendente'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            ) : null}
            {canClose ? (
              <DropdownMenuItem onSelect={() => setDialog('finish')}>
                <CheckCircle2 />
                Finalizar
              </DropdownMenuItem>
            ) : null}
            {canCancel ? (
              <DropdownMenuItem variant="destructive" onSelect={() => setDialog('cancel')}>
                <Ban />
                Cancelar
              </DropdownMenuItem>
            ) : null}
            {canClose ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem disabled={downloading} onSelect={() => doDownload('pdf')}>
              <Download />
              Baixar conversa
            </DropdownMenuItem>
            <DropdownMenuItem disabled={downloading} onSelect={() => doDownload('txt')}>
              <FileText />
              Baixar como texto (.txt)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {archived && chat.assessment !== null ? (
          <Stars value={chat.assessment} className="mr-1" />
        ) : null}

        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={infoOpen ? 'Ocultar informações' : 'Mostrar informações'}
          aria-pressed={infoOpen}
          onClick={onToggleInfo}
          className={cn(infoOpen && 'text-primary')}
        >
          <Info />
        </Button>
      </div>

      {dialog === 'transfer' ? (
        <TransferChatDialog
          chat={chat}
          onClose={() => setDialog(null)}
          onTransferred={() => showToast('Conversa transferida')}
        />
      ) : null}
      {dialog === 'finish' || dialog === 'cancel' ? (
        <ArchiveChatDialog
          chat={chat}
          canceled={dialog === 'cancel'}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'ticket' ? (
        <LinkTicketDialog chat={chat} onClose={() => setDialog(null)} />
      ) : null}
      <ContactSheet chatId={chat.id} open={contactOpen} onClose={() => setContactOpen(false)} />
      {pausedAssume ? (
        <Dialog open onClose={() => setPausedAssume(null)} title="Você está em pausa">
          <p className="text-sm text-muted-foreground">
            Encerrar a pausa e assumir a conversa com{' '}
            <span className="font-medium text-foreground">{displayName}</span>? Seu status
            passa a Disponível agora.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setPausedAssume(null)}>
              Continuar em pausa
            </Button>
            <Button
              size="sm"
              disabled={assume.isPending}
              onClick={() => doAssume({ ...pausedAssume, endPause: true })}
            >
              {assume.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
              Encerrar a pausa e assumir
            </Button>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}

// ── Bolhas ──────────────────────────────────────────────────────────────────

function MessageBubble({
  message,
  whatsapp,
  systemDetails,
  onResend,
  resending,
}: {
  message: ChatMessage;
  /** conversa do WhatsApp: a formatação de lá no texto */
  whatsapp: boolean;
  /** spec 060: o switch "Detalhes do sistema" — as notas do sistema abertas */
  systemDetails: boolean;
  /** spec 028: reenviar a resposta que falhou — null = quem vê não pode */
  onResend: ((messageId: number) => void) | null;
  resending: boolean;
}) {
  // spec 060: o que a mensagem é (message-kind.ts) — o comentário de um
  // atendente, o registro do sistema e o que o sistema mandou ao cliente não
  // saem mais no mesmo amarelo
  const kind = messageKind(message);
  if (kind === 'bot') return <BotBubble message={message} />;
  if (kind === 'system-note') return <SystemNote message={message} expanded={systemDetails} />;
  if (kind === 'system-event') {
    return (
      <div className="py-0.5 text-center text-xs text-muted-foreground">
        {/* registro só da equipe (motivo do encerramento, spec 013) — o
            cliente nunca vê esta linha no widget */}
        {message.internal ? <span className="mr-1 font-semibold text-[#D48806]">Interno ·</span> : null}
        {message.text}
        <span className="ml-1.5 tabular-nums opacity-70">
          <MessageTime at={message.createdAt} />
        </span>
      </div>
    );
  }

  if (kind === 'internal-comment') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[75%] rounded-lg rounded-br-sm border border-[#FFE58F] bg-[#FFFBE6] px-3 py-2">
          <div className="text-[11px] font-semibold text-[#D48806]">Comentário interno</div>
          <div className="text-sm break-words whitespace-pre-wrap text-foreground">
            {message.text}
          </div>
          <div className="mt-0.5 text-right text-[11px] text-muted-foreground tabular-nums">
            {message.authorName ? `${message.authorName} · ` : ''}
            <MessageTime at={message.createdAt} />
          </div>
        </div>
      </div>
    );
  }

  const inbound = message.direction === 'inbound';
  // spec 060: o que o sistema mandou ao cliente (o modelo do disparo, o menu do
  // bot do ERP) é conversa, mas não é da equipe: saída, em tom neutro, com selo
  const automatic = kind === 'automatic';
  const neutral = inbound || automatic;
  return (
    <div className={cn('flex', inbound ? 'justify-start' : 'justify-end')}>
      <div
        className={cn(
          'max-w-[75%] rounded-lg px-3 py-2',
          inbound
            ? 'rounded-bl-sm border bg-card text-foreground'
            : automatic
              ? 'rounded-br-sm border bg-muted text-foreground'
              : 'rounded-br-sm bg-primary text-primary-foreground'
        )}
      >
        {automatic ? (
          <div className="text-[11px] font-semibold text-muted-foreground">Mensagem automática</div>
        ) : message.authorName ? (
          <div
            className={cn(
              'text-[11px] font-semibold',
              inbound ? 'text-muted-foreground' : 'text-primary-foreground/80'
            )}
          >
            {message.authorName}
          </div>
        ) : null}
        {isMediaContent(message) ? (
          // image/sticker/audio/video/document (v8) — caption renderizada
          // dentro do MessageMedia; o text é só fallback/preview.
          <MessageMedia message={message} inbound={inbound} />
        ) : (
          // text/location/unknown: texto com URLs http(s) linkificadas
          // (localização chega como texto com link de mapa) e, no WhatsApp, a
          // formatação de lá — `*negrito*` sai em negrito, não com asteriscos
          <div className="text-sm break-words whitespace-pre-wrap">
            {whatsapp ? <WhatsappText text={message.text} /> : <LinkifiedText text={message.text} />}
          </div>
        )}
        <div
          className={cn(
            'mt-0.5 flex items-center justify-end gap-1 text-[11px] tabular-nums',
            neutral ? 'text-muted-foreground' : 'text-primary-foreground/70'
          )}
        >
          <MessageTime at={message.createdAt} />
          {!inbound && message.deliveryStatus !== null ? (
            <DeliveryTicks status={message.deliveryStatus} sentAt={message.createdAt} neutral={automatic} />
          ) : null}
        </div>
        {!inbound && message.deliveryStatus === 'failed' ? (
          <FailedLine message={message} onResend={onResend} resending={resending} neutral={automatic} />
        ) : null}
      </div>
    </div>
  );
}

/**
 * O registro do sistema com detalhes (spec 060): a transferência do bot externo,
 * com motivo, variáveis e a conversa com ele, e a conversa na Blip antes do
 * nosso bot. Recolhido, é uma linha (a primeira da nota) com "ver detalhes";
 * aberto (por ela ou pelo switch), o bloco inteiro, em cinza — o amarelo é do
 * comentário de um atendente. Só a equipe vê: a nota é interna.
 */
function SystemNote({ message, expanded }: { message: ChatMessage; expanded: boolean }) {
  const [open, setOpen] = useState(false);
  if (!expanded && !open) {
    return (
      <div className="py-0.5 text-center text-xs text-muted-foreground" data-system-note="recolhida">
        <span className="mr-1 font-semibold">Sistema ·</span>
        {noteSummary(message.text)}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="ml-1.5 font-medium text-foreground/70 underline-offset-2 outline-none hover:underline focus-visible:underline"
        >
          ver detalhes
        </button>
        <span className="ml-1.5 tabular-nums opacity-70"><MessageTime at={message.createdAt} /></span>
      </div>
    );
  }
  return (
    <div className="rounded-lg border bg-muted/60 px-3 py-2" data-system-note="aberta">
      <div className="flex items-center justify-between gap-2 text-[11px] font-semibold text-muted-foreground">
        <span>Registro do sistema · só a equipe vê</span>
        {expanded ? null : (
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="font-medium underline-offset-2 outline-none hover:underline focus-visible:underline"
          >
            recolher
          </button>
        )}
      </div>
      <div className="text-xs break-words whitespace-pre-wrap text-foreground">{message.text}</div>
      <div className="mt-0.5 text-right text-[11px] text-muted-foreground tabular-nums">
        <MessageTime at={message.createdAt} />
      </div>
    </div>
  );
}

/**
 * A fala do bot externo (spec 028, FR-005): balão de SAÍDA, mas não da equipe —
 * outra cor e o selo "bot", para ninguém achar que um atendente respondeu.
 */
function BotBubble({ message }: { message: ChatMessage }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[75%] rounded-lg rounded-br-sm border border-violet-200 bg-violet-50 px-3 py-2 text-foreground">
        <div className="flex items-center gap-1 text-[11px] font-semibold text-violet-700">
          <Bot className="size-3.5" aria-hidden />
          bot
        </div>
        {/* formatado como no WhatsApp, e os botões/lista como opções só de leitura */}
        <BotMessageBody text={message.text} />
        <div className="mt-0.5 text-right text-[11px] text-muted-foreground tabular-nums">
          <MessageTime at={message.createdAt} />
        </div>
      </div>
    </div>
  );
}

/**
 * "Não entregue" (spec 028, FR-006): o motivo curto e o "tentar de novo" — a
 * resposta volta como mensagem nova, no fim da conversa. Reenviada, o botão
 * some (o servidor recusa o segundo reenvio de qualquer forma).
 */
function FailedLine({
  message,
  onResend,
  resending,
  neutral = false,
}: {
  message: ChatMessage;
  onResend: ((messageId: number) => void) | null;
  resending: boolean;
  /** spec 060: na bolha neutra da mensagem automática, e não na teal */
  neutral?: boolean;
}) {
  const reason = deliveryErrorText(message.deliveryError);
  const code = message.deliveryError?.code ?? null;
  const resendable = message.authorType === 'attendant' && !message.internal;
  return (
    <div
      className={cn(
        'mt-1 flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5 border-t pt-1 text-[11px]',
        neutral ? 'border-border' : 'border-primary-foreground/25'
      )}
    >
      <span
        className={cn('font-semibold', neutral ? 'text-destructive' : 'text-primary-foreground')}
        title={code ? `código: ${code}` : undefined}
      >
        Não entregue · {reason}
      </span>
      {!resendable ? null : message.resentAt !== null ? (
        <span className="text-primary-foreground/80">Reenviada</span>
      ) : onResend ? (
        <button
          type="button"
          onClick={() => onResend(message.id)}
          disabled={resending}
          className="inline-flex items-center gap-1 rounded-sm bg-primary-foreground px-1.5 py-0.5 font-semibold text-primary outline-none hover:bg-primary-foreground/90 focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-60"
        >
          {resending ? <Spinner className="size-3" /> : <RotateCcw className="size-3" aria-hidden />}
          Tentar de novo
        </button>
      ) : null}
    </div>
  );
}

/**
 * Ticks de entrega do WhatsApp (v6) — só mensagens outbound com ack. Na bolha
 * teal, o "cinza" é o branco translúcido do timestamp; o tick de leitura usa o
 * petróleo da marca (teal escuro — o teal claro sumiria sobre a bolha).
 *
 * 20/10: "Enviada" não é "chegou". Sem confirmação do WhatsApp depois do prazo
 * (`deliveryView`), a bolha diz isso em texto — foi o caso das cobranças para
 * números sem o 55, que nunca chegaram e ficaram com o tick de enviada.
 */
function DeliveryTicks({
  status,
  sentAt,
  neutral = false,
}: {
  status: DeliveryStatus;
  sentAt: string | Date;
  /** spec 060: na bolha neutra da mensagem automática, os tons que se leem sobre ela */
  neutral?: boolean;
}) {
  const view = deliveryView(status, sentAt, Date.now());
  const faint = neutral ? 'text-muted-foreground' : 'text-primary-foreground/70';
  if (view === 'unconfirmed') {
    return (
      <span
        title="Sem confirmação de entrega — o WhatsApp não confirmou que a mensagem chegou. Confira o número do contato."
        aria-label="Sem confirmação de entrega"
        className={cn(
          'inline-flex shrink-0 items-center gap-0.5',
          neutral ? 'text-[#D48806]' : 'text-[#FFE58F]'
        )}
      >
        <AlertTriangle className="size-3.5" />
        sem confirmação
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span
        title="Falha no envio — a mensagem não chegou ao WhatsApp"
        aria-label="Falha no envio"
        className="shrink-0"
      >
        <AlertTriangle className={cn('size-3.5', neutral ? 'text-destructive' : 'text-[#FFA39E]')} />
      </span>
    );
  }
  if (status === 'read') {
    return (
      <CheckCheck
        aria-label="Lida"
        className={cn('size-3.5 shrink-0', neutral ? 'text-primary' : 'text-[#03363d]')}
      />
    );
  }
  if (status === 'delivered') {
    return (
      <CheckCheck aria-label="Entregue" className={cn('size-3.5 shrink-0', faint)} />
    );
  }
  return (
    <span title="Enviada — aguardando a confirmação do WhatsApp" className="shrink-0">
      <Check aria-label="Enviada" className={cn('size-3.5', faint)} />
    </span>
  );
}

// ── Composer ────────────────────────────────────────────────────────────────

/** Legenda faz sentido para imagem/vídeo/documento (não para áudio). */
function acceptsCaption(file: File): boolean {
  return !file.type.startsWith('audio/');
}

const ATTACH_ACCEPT =
  'image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip';

/**
 * Composer completo do atendente: texto livre, anexo, comentário interno e a
 * troca automática para modelo HSM com a janela de 24h fechada. Exportado para
 * a aba "Conversas" do ticket (v12) — essas regras existem UMA vez.
 */
export function ChatComposer({ chat, isMine }: { chat: ChatDetail; isMine: boolean }) {
  const send = useSendChatMessage(chat.id);
  const sendMedia = useSendChatMedia(chat.id);
  const [text, setText] = useState('');
  const [internal, setInternal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Anexo em preparação (preview antes do envio).
  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const archived = chat.status === 'archived';
  const canSendVisible = chat.status === 'in_attendance' && isMine;
  // Sem posse da conversa (Entrada ou responsável é outro): só comentário interno.
  const internalOnly = !archived && !canSendVisible;
  const effectiveInternal = internalOnly || internal;

  // Janela de 24h do WhatsApp (v8): fora dela, só modelo HSM reabre a conversa.
  const isWhatsapp = chat.origin === 'whatsapp';
  const windowQ = useQuery({
    ...whatsappWindowOptions(chat.id),
    enabled: isWhatsapp && !archived,
  });
  const windowClosed = isWhatsapp && windowQ.data?.open === false;

  // Respostas rápidas (PoolTrack, 02/10): `/` na caixa ou o botão; o texto
  // entra para revisão, nunca é enviado. Sem caixa de texto (encerrada, ou só
  // modelo com a janela fechada), nada é buscado.
  const quick = useQuickReplyComposer({
    chatId: chat.id,
    enabled: !archived && !(windowClosed && canSendVisible),
    text,
    setText,
    textareaRef,
  });

  const submit = () => {
    const value = text.trim();
    if (!value || send.isPending || archived) return;
    setError(null);
    send.mutate(
      { text: value, internal: effectiveInternal },
      { onSuccess: () => setText(''), onError: (e) => setError(errorMessage(e)) }
    );
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // com a lista das respostas rápidas aberta, as teclas são dela (Enter
    // escolhe e NUNCA envia)
    if (quick.handleKeyDown(e)) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const pickFile = (files: FileList | null) => {
    const next = files?.[0];
    if (!next) return;
    setError(null);
    if (next.size > MAX_UPLOAD_BYTES) {
      setError(`O arquivo excede o limite de ${MAX_UPLOAD_LABEL}.`);
      return;
    }
    setFile(next);
    setCaption('');
  };

  const cancelFile = () => {
    setFile(null);
    setCaption('');
    setError(null);
  };

  const submitFile = () => {
    if (!file || sendMedia.isPending) return;
    setError(null);
    sendMedia.mutate(
      { file, caption: caption.trim() || undefined },
      { onSuccess: cancelFile, onError: (e) => setError(errorMessage(e)) }
    );
  };

  if (archived) {
    return (
      <div className="shrink-0 border-t bg-muted px-4 py-3 text-center text-sm text-muted-foreground">
        Conversa encerrada — não é possível enviar novas mensagens.
      </div>
    );
  }

  // Janela fechada e posse da conversa: composer de modelo HSM no lugar do
  // texto livre, com aba de nota interna — a nota nunca vai ao WhatsApp,
  // então segue permitida (sem posse, o composer interno continua).
  if (windowClosed && canSendVisible) {
    return <WindowClosedComposer chatId={chat.id} phoneNumberId={chat.waPhoneNumberId} />;
  }

  // Anexo visível nas mesmas condições do envio de mensagem ao solicitante.
  const canAttach = canSendVisible && !windowClosed && !effectiveInternal;

  return (
    <div
      className={cn(
        'relative shrink-0 border-t px-3 pt-2 pb-2.5 transition-colors',
        effectiveInternal && 'bg-[#FFFBE6]'
      )}
    >
      {quick.slashPopup}
      {internalOnly ? (
        <p className="mb-1.5 text-xs text-muted-foreground">
          Assuma a conversa para responder ao solicitante — apenas comentários internos são
          permitidos.
        </p>
      ) : null}

      {file ? (
        // Preview do anexo: substitui a linha de texto até enviar/cancelar.
        <div className="flex items-center gap-2 rounded-md border border-input bg-card px-2.5 py-2">
          <Paperclip className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">{file.name}</span>
            <span className="block text-[11px] text-muted-foreground">
              {formatFileSize(file.size)}
            </span>
          </span>
          {acceptsCaption(file) ? (
            <Input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Legenda (opcional)"
              aria-label="Legenda do anexo"
              className="h-8 w-44 shrink-0 text-sm"
              disabled={sendMedia.isPending}
            />
          ) : null}
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Remover anexo"
            onClick={cancelFile}
            disabled={sendMedia.isPending}
          >
            <X />
          </Button>
          <Button
            size="icon-sm"
            aria-label="Enviar arquivo"
            onClick={submitFile}
            disabled={sendMedia.isPending}
          >
            {sendMedia.isPending ? (
              <Spinner className="size-4 border-primary-foreground" />
            ) : (
              <SendHorizonal />
            )}
          </Button>
        </div>
      ) : (
        <div className="flex items-end gap-2">
          {canAttach ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept={ATTACH_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  pickFile(e.target.files);
                  e.target.value = '';
                }}
              />
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Anexar arquivo"
                title={`Anexar arquivo (máx. ${MAX_UPLOAD_LABEL})`}
                className="mb-0.5 text-muted-foreground"
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip />
              </Button>
            </>
          ) : null}
          {quick.button}
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              quick.onTextChange(e.target);
            }}
            onKeyDown={onKeyDown}
            {...quick.textareaProps}
            rows={1}
            placeholder={
              effectiveInternal ? 'Escreva um comentário interno…' : 'Escreva uma mensagem…'
            }
            aria-label="Mensagem"
            className="field-sizing-content max-h-24 min-h-9 w-full flex-1 resize-none rounded-md border border-input bg-card px-3 py-2 text-sm shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          />
          <Button
            size="icon-sm"
            aria-label="Enviar mensagem"
            onClick={submit}
            disabled={send.isPending || text.trim() === ''}
          >
            {send.isPending ? (
              <Spinner className="size-4 border-primary-foreground" />
            ) : (
              <SendHorizonal />
            )}
          </Button>
        </div>
      )}

      <div className="mt-1.5 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5">
          <Switch
            id="chat-internal"
            size="sm"
            checked={effectiveInternal}
            disabled={internalOnly}
            onCheckedChange={setInternal}
          />
          <Label
            htmlFor="chat-internal"
            className={cn(
              'text-xs',
              effectiveInternal ? 'text-[#D48806]' : 'text-muted-foreground'
            )}
          >
            Comentário interno
          </Label>
        </span>
        <span className="text-[11px] text-muted-foreground/70">
          Enter envia · Shift+Enter quebra linha · / respostas rápidas
        </span>
      </div>

      {quick.warning ? <div className="mt-1">{quick.warning}</div> : null}

      {error ? (
        <p className="mt-1 text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Composer da JANELA DE 24H FECHADA (v8): só um modelo aprovado reabre a
 * conversa no WhatsApp, mas notas internas continuam permitidas (nunca tocam
 * o WhatsApp) — abas "Modelo" | "Nota interna" para não perder essa via.
 */
function WindowClosedComposer({
  chatId,
  phoneNumberId,
}: {
  chatId: number;
  /** o número da conversa: os modelos dele (spec 025) */
  phoneNumberId: string | null;
}) {
  const [tab, setTab] = useState<'template' | 'internal'>('template');

  return (
    <div className="shrink-0 border-t bg-muted/40">
      <div className="flex items-center justify-between gap-2 px-3 pt-2.5">
        <p className="text-xs text-muted-foreground">
          Janela de 24h fechada — envie um modelo para reabrir a conversa.
        </p>
        <div className="flex shrink-0 items-center gap-1" role="tablist" aria-label="Tipo de envio">
          <WindowClosedTab active={tab === 'template'} onClick={() => setTab('template')}>
            Modelo
          </WindowClosedTab>
          <WindowClosedTab active={tab === 'internal'} onClick={() => setTab('internal')}>
            Nota interna
          </WindowClosedTab>
        </div>
      </div>
      {tab === 'template' ? (
        <TemplateComposer chatId={chatId} phoneNumberId={phoneNumberId} />
      ) : (
        <InternalNoteComposer chatId={chatId} />
      )}
    </div>
  );
}

function WindowClosedTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'rounded-md px-2.5 py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/50',
        active
          ? 'bg-card text-foreground shadow-xs'
          : 'text-muted-foreground hover:text-foreground'
      )}
    >
      {children}
    </button>
  );
}

/**
 * Composer de MODELO HSM (v8): janela de 24h fechada — só um modelo aprovado
 * reabre a conversa. Envio invalida a query da janela (o hook da mutation),
 * que ao voltar {open:true} devolve o composer de texto livre.
 */
function TemplateComposer({
  chatId,
  phoneNumberId,
}: {
  chatId: number;
  phoneNumberId: string | null;
}) {
  const sendTemplate = useSendChatTemplate(chatId);
  const [template, setTemplate] = useState<ChatTemplateBody | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    if (!template || sendTemplate.isPending) return;
    setError(null);
    sendTemplate.mutate(template, { onError: (e) => setError(errorMessage(e)) });
  };

  return (
    <div className="space-y-2.5 px-3 py-3">
      <TemplatePicker
        idPrefix="cw-tpl"
        {...(phoneNumberId ? { phoneNumberId } : {})}
        onChange={setTemplate}
        disabled={sendTemplate.isPending}
      />

      {error ? (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex justify-end">
        <Button size="sm" onClick={submit} disabled={sendTemplate.isPending || !template}>
          {sendTemplate.isPending ? (
            <Spinner className="size-4 border-primary-foreground" />
          ) : (
            <SendHorizonal />
          )}
          Enviar modelo
        </Button>
      </div>
    </div>
  );
}

/**
 * Nota interna com a janela fechada: mesma mutation do composer normal com
 * internal:true fixo (a API nunca encaminha internal ao WhatsApp) — visual
 * âmbar do modo "comentário interno".
 */
function InternalNoteComposer({ chatId }: { chatId: number }) {
  const send = useSendChatMessage(chatId);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const value = text.trim();
    if (!value || send.isPending) return;
    setError(null);
    send.mutate(
      { text: value, internal: true },
      { onSuccess: () => setText(''), onError: (e) => setError(errorMessage(e)) }
    );
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="mt-2 bg-[#FFFBE6] px-3 pt-2 pb-2.5">
      <div className="flex items-end gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder="Escreva um comentário interno…"
          aria-label="Comentário interno"
          className="field-sizing-content max-h-24 min-h-9 w-full flex-1 resize-none rounded-md border border-input bg-card px-3 py-2 text-sm shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
        />
        <Button
          size="icon-sm"
          aria-label="Enviar comentário interno"
          onClick={submit}
          disabled={send.isPending || text.trim() === ''}
        >
          {send.isPending ? (
            <Spinner className="size-4 border-primary-foreground" />
          ) : (
            <SendHorizonal />
          )}
        </Button>
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <span className="text-xs text-[#D48806]">
          Comentário interno — não é enviado ao solicitante.
        </span>
        <span className="text-[11px] text-muted-foreground/70">
          Enter envia · Shift+Enter quebra linha
        </span>
      </div>
      {error ? (
        <p className="mt-1 text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
